import { OverlayRef } from '@angular/cdk/overlay';
import { Injectable, Type } from '@angular/core';
import {
  MatSnackBar,
  MatSnackBarConfig,
  MatSnackBarRef,
} from '@angular/material/snack-bar';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { AlertSnackBarComponent } from './alert-snack-bar/alert-snack-bar.component';
import { AlertSnackBarData } from './types';

// Removes the need to cast type when trying to use 'panelClass' as an array.
type DefaultConfig = MatSnackBarConfig & { panelClass: string[] };
type SnackBarData = Omit<AlertSnackBarData, 'severity'>;

@Injectable({
  providedIn: 'root',
})
export class SnackBarService {
  // Update default position to top-right
  private readonly defaultConfig: DefaultConfig = {
    panelClass: ['reset-mat-snackbar'],
    verticalPosition: 'top',
    horizontalPosition: 'right',
  };

  constructor(
    private readonly matSnackBar: MatSnackBar,
    private readonly router: Router
  ) {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationStart))
      .subscribe(() => {
        matSnackBar._openedSnackBarRef?.dismiss();
      });
  }

  /**
   * Dismisses the currently visible snack bar, if any.
   */
  dismiss(): void {
    this.matSnackBar.dismiss();
  }

  openAlert(
    data: AlertSnackBarData,
    config?: Omit<MatSnackBarConfig, 'data'>
  ): MatSnackBarRef<AlertSnackBarComponent> {
    return this.openWithComponent(AlertSnackBarComponent, { ...config, data });
  }

  /**
   * Shows error snackbar with cross close button (duration: 3s by default)
   * @example
   * ```typescript
   * // Basic error
   * showError({ message: $localize`Save failed` });
   *
   * // With action button
   * showError({
   *   message: $localize`Upload failed`,
   *   actionType: 'action',
   *   actionBtnText: 'Retry'
   * });
   * ```
   */
  showError(data: SnackBarData, config?: Omit<MatSnackBarConfig, 'data'>) {
    return this.openAlert(
      { severity: 'error', actionType: 'close', ...data },
      { duration: 3000, ...config }
    );
  }

  /**
   * Shows success snackbar with cross close button (duration: 3s by default)
   * @example
   * ```typescript
   * // Basic success
   * showSuccess({ message: $localize`Data saved!` });
   *
   * // With action button
   * showSuccess({
   *   message: $localize`Upload complete`,
   *   actionType: 'action',
   *   actionBtnText: 'View'
   * });
   * ```
   */
  showSuccess(data: SnackBarData, config?: Omit<MatSnackBarConfig, 'data'>) {
    return this.openAlert(
      { severity: 'success', actionType: 'close', ...data },
      { duration: 3000, ...config }
    );
  }

  /**
   * Shows info snackbar with cross close button (duration: 3s by default)
   * @example
   * ```typescript
   * // Basic info
   * showInfo({ message: $localize`Data saved!` });
   *
   * // With action button
   * showInfo({
   *   message: $localize`Upload complete`,
   *   actionType: 'action',
   *   actionBtnText: 'View'
   * });
   * ```
   */
  showInfo(data: SnackBarData, config?: Omit<MatSnackBarConfig, 'data'>) {
    return this.openAlert(
      { severity: 'info', actionType: 'close', ...data },
      { duration: 3000, ...config }
    );
  }

  /**
   * Opens provided component inside [MatSnackBar](https://material.angular.io/components/snack-bar/overview).
   * Most of the default Material styling is removed to simplify customization.
   */
  openWithComponent<T, D>(
    component: Type<T>,
    config?: MatSnackBarConfig<D>
  ): MatSnackBarRef<T> {
    const mergedConfig = this.mergeWithDefaultConfig(config);

    const snackBarRef = this.matSnackBar.openFromComponent(
      component,
      mergedConfig
    );

    if (mergedConfig.verticalPosition === 'top') {
      const overlayRef: OverlayRef = snackBarRef['_overlayRef'];
      overlayRef.addPanelClass('top-offset');
    }

    return snackBarRef;
  }

  private mergeWithDefaultConfig(config: MatSnackBarConfig = {}) {
    return {
      ...this.defaultConfig,
      ...config,
      panelClass: this.defaultConfig.panelClass.concat(config.panelClass || []),
    };
  }
}
